/**
 * Braynewave's costs in its console (R189; docs/specs/cloud/platform-costs.md): costs and their categories, the PDFs
 * in the private `platform-costs` bucket, the platform's Claude key through the `platform-costs` function, and the
 * reading of a supplier's invoice. Platform admins at two-step sign-in only; the database refuses everyone else and
 * records every change (20261103200000_platform_costs.sql). This file only asks.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  PLATFORM_COSTS_FUNCTION,
  PLATFORM_KEY_PROVIDER,
  bytesToBase64,
  normaliseSupplierInvoice,
  type CostCategory,
  type CostDraft,
  type SupplierInvoiceReading,
} from '@teslyn/domain';
import { adminError } from './api.ts';

export const COSTS_BUCKET = 'platform-costs';

export interface Cost {
  readonly id: string;
  readonly supplier: string;
  readonly supplierAbn: string | null;
  readonly invoiceNumber: string;
  readonly invoiceDate: string;
  readonly dueDate: string | null;
  readonly description: string;
  readonly categoryId: string;
  readonly currency: string;
  readonly amountExGst: number;
  readonly gst: number;
  readonly total: number;
  readonly audTotal: number | null;
  readonly paidOn: string | null;
  readonly notes: string;
  readonly pdfPath: string | null;
  readonly pdfName: string | null;
  readonly readBy: 'typed' | 'text' | 'claude';
  readonly deletedAt: string | null;
}

function fail(error: { message: string; code?: string } | null): void {
  if (error) throw new Error(adminError(error));
}

const num = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0));
const numOrNull = (v: unknown): number | null => (v === null || v === undefined ? null : num(v));

export function costFromRow(r: Record<string, unknown>): Cost {
  return {
    id: String(r.id),
    supplier: String(r.supplier ?? ''),
    supplierAbn: (r.supplier_abn as string | null) ?? null,
    invoiceNumber: String(r.invoice_number ?? ''),
    invoiceDate: String(r.invoice_date),
    dueDate: (r.due_date as string | null) ?? null,
    description: String(r.description ?? ''),
    categoryId: String(r.category_id),
    currency: String(r.currency ?? 'AUD'),
    amountExGst: num(r.amount_ex_gst),
    gst: num(r.gst),
    total: num(r.total),
    audTotal: numOrNull(r.aud_total),
    paidOn: (r.paid_on as string | null) ?? null,
    notes: String(r.notes ?? ''),
    pdfPath: (r.pdf_path as string | null) ?? null,
    pdfName: (r.pdf_name as string | null) ?? null,
    readBy: (r.read_by as Cost['readBy']) ?? 'typed',
    deletedAt: (r.deleted_at as string | null) ?? null,
  };
}

export async function loadCategories(sb: SupabaseClient): Promise<CostCategory[]> {
  const { data, error } = await sb
    .from('platform_cost_category')
    .select('id, code, label, sort, retired')
    .order('sort', { ascending: true });
  fail(error);
  return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
    id: String(r.id),
    code: (r.code as string | null) ?? null,
    label: String(r.label),
    sort: num(r.sort),
    retired: r.retired === true,
  }));
}

/** Add a category (no id) or rename or retire one. */
export async function saveCategory(
  sb: SupabaseClient,
  c: { readonly label: string; readonly retired: boolean; readonly sort?: number },
  id: string | null,
): Promise<void> {
  const values = {
    label: c.label.trim(),
    retired: c.retired,
    ...(c.sort === undefined ? {} : { sort: c.sort }),
  };
  const { error } = id
    ? await sb.from('platform_cost_category').update(values).eq('id', id)
    : await sb.from('platform_cost_category').insert(values);
  fail(error);
}

export async function loadCosts(sb: SupabaseClient): Promise<Cost[]> {
  const { data, error } = await sb
    .from('platform_cost')
    .select('*')
    .order('invoice_date', { ascending: false });
  fail(error);
  return ((data ?? []) as Record<string, unknown>[]).map(costFromRow);
}

/** A PDF's object name: `costs/<cost id>/<safe name>.pdf` (the bucket's policy and the table's check). */
export function costPdfPath(costId: string, fileName: string): string {
  const base = fileName
    .replace(/\.pdf$/i, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, 70);
  return `costs/${costId}/${base || 'invoice'}.pdf`;
}

const costValues = (d: CostDraft): Record<string, unknown> => ({
  supplier: d.supplier.trim(),
  supplier_abn: d.supplierAbn.replace(/[\s.-]/g, '') || null,
  invoice_number: d.invoiceNumber.trim(),
  invoice_date: d.invoiceDate,
  due_date: d.dueDate || null,
  description: d.description.trim(),
  category_id: d.categoryId,
  currency: d.currency,
  amount_ex_gst: d.amountExGst,
  gst: d.gst,
  total: d.total,
  aud_total: d.currency === 'AUD' ? null : d.audTotal,
  paid_on: d.paidOn || null,
  notes: d.notes,
});

/** A PDF chosen in the form: its bytes and name, to upload with the cost. */
export interface CostPdf {
  readonly bytes: Uint8Array;
  readonly name: string;
}

/**
 * Save a cost (no id: a new one), with a new PDF when one is given: the PDF is uploaded first under the cost's id
 * and removed again if the row is refused; a replaced PDF is removed after the row points at the new one.
 */
export async function saveCost(
  sb: SupabaseClient,
  d: CostDraft,
  opts: {
    readonly id: string | null;
    readonly pdf: CostPdf | null;
    readonly oldPdfPath: string | null;
    readonly readBy: Cost['readBy'];
  },
): Promise<string> {
  const id = opts.id ?? crypto.randomUUID();
  let path: string | null = null;
  if (opts.pdf) {
    path = costPdfPath(id, opts.pdf.name);
    if (path === opts.oldPdfPath)
      path = costPdfPath(id, `${opts.pdf.name.replace(/\.pdf$/i, '')}-${Date.now()}`);
    const up = await sb.storage
      .from(COSTS_BUCKET)
      .upload(path, new Blob([opts.pdf.bytes as BlobPart], { type: 'application/pdf' }), {
        contentType: 'application/pdf',
        upsert: false,
      });
    if (up.error) throw new Error(`The PDF was not stored: ${up.error.message}`);
  }
  const values = {
    ...costValues(d),
    ...(path ? { pdf_path: path, pdf_name: opts.pdf!.name.slice(0, 200) } : {}),
    ...(opts.id ? {} : { read_by: opts.readBy }),
  };
  const { error } = opts.id
    ? await sb.from('platform_cost').update(values).eq('id', opts.id)
    : await sb.from('platform_cost').insert({ id, ...values });
  if (error) {
    if (path) await sb.storage.from(COSTS_BUCKET).remove([path]);
    fail(error);
  }
  if (path && opts.oldPdfPath) await sb.storage.from(COSTS_BUCKET).remove([opts.oldPdfPath]);
  return id;
}

/** Delete softly (the totals and the profit and loss leave it out; it can be restored), or restore. */
export async function setCostDeleted(sb: SupabaseClient, id: string, deleted: boolean): Promise<void> {
  const { error } = await sb
    .from('platform_cost')
    .update({ deleted_at: deleted ? new Date().toISOString() : null })
    .eq('id', id);
  fail(error);
}

/** A link to a cost's PDF that lasts 60 seconds. */
export async function costPdfLink(sb: SupabaseClient, path: string): Promise<string> {
  const { data, error } = await sb.storage.from(COSTS_BUCKET).createSignedUrl(path, 60);
  if (error || !data?.signedUrl) throw new Error('The PDF could not be opened. Try again.');
  return data.signedUrl;
}

// ---------------------------------------------------------------- the platform's Claude key
export interface PlatformKeyStatus {
  readonly lastFour: string;
  readonly setAt: string;
  readonly lastOkAt: string | null;
  readonly lastError: string | null;
  readonly lastErrorAt: string | null;
}

/** Whether the platform's Claude key is set (never the key: the table does not give it to anyone). */
export async function loadKeyStatus(sb: SupabaseClient): Promise<PlatformKeyStatus | null> {
  const { data, error } = await sb
    .from('platform_integration')
    .select('provider, last_four, set_at, last_ok_at, last_error, last_error_at')
    .eq('provider', PLATFORM_KEY_PROVIDER)
    .maybeSingle();
  fail(error);
  if (!data) return null;
  const r = data as Record<string, unknown>;
  return {
    lastFour: String(r.last_four ?? ''),
    setAt: String(r.set_at),
    lastOkAt: (r.last_ok_at as string | null) ?? null,
    lastError: (r.last_error as string | null) ?? null,
    lastErrorAt: (r.last_error_at as string | null) ?? null,
  };
}

/** The function's answer, or its words and status when it refused. */
async function call(
  sb: SupabaseClient,
  body: Record<string, unknown>,
): Promise<
  { ok: true; data: Record<string, unknown> } | { ok: false; status: number; message: string; code: string }
> {
  const { data, error } = await sb.functions.invoke<Record<string, unknown>>(PLATFORM_COSTS_FUNCTION, {
    body,
    timeout: 150_000,
  });
  if (!error) return { ok: true, data: data ?? {} };
  let message = error.message;
  let status = 0;
  let code = 'failed';
  const ctx = (error as { context?: unknown }).context;
  if (ctx instanceof Response) {
    status = ctx.status;
    try {
      const j = (await ctx.json()) as { error?: string; status?: string };
      if (j.error) message = j.error;
      if (j.status) code = j.status;
    } catch {
      /* keep the generic message */
    }
  }
  return { ok: false, status, message, code };
}

/** Store or replace the key. It is sent once, to the function, and never shown again. */
export async function setPlatformKey(sb: SupabaseClient, apiKey: string): Promise<void> {
  const r = await call(sb, { action: 'key_set', api_key: apiKey });
  if (!r.ok) throw new Error(r.message);
}

export async function removePlatformKey(sb: SupabaseClient): Promise<void> {
  const r = await call(sb, { action: 'key_remove' });
  if (!r.ok) throw new Error(r.message);
}

export type ClaudeReadOutcome =
  | { readonly kind: 'read'; readonly reading: SupplierInvoiceReading; readonly model: string }
  | { readonly kind: 'not_set_up' | 'failed'; readonly message: string };

/** Have Claude read a supplier's invoice with the platform's key (the PDF as it is). */
export async function readWithClaude(sb: SupabaseClient, bytes: Uint8Array): Promise<ClaudeReadOutcome> {
  const r = await call(sb, { action: 'read', media_type: 'application/pdf', data: bytesToBase64(bytes) });
  if (r.ok && r.data.status === 'ok')
    return {
      kind: 'read',
      reading: normaliseSupplierInvoice({ values: r.data.values, unsure: r.data.unsure }),
      model: typeof r.data.model === 'string' ? r.data.model : '',
    };
  if (!r.ok && (r.status === 409 || r.code === 'not_set_up'))
    return { kind: 'not_set_up', message: r.message };
  return { kind: 'failed', message: r.ok ? 'Claude did not answer.' : r.message };
}

// ---------------------------------------------------------------- Braynewave's book settings (decision 1001)
/** Whether Braynewave is registered for GST (false until the platform admin switches it; platform_books). */
export async function loadGstRegistered(sb: SupabaseClient): Promise<boolean> {
  const { data, error } = await sb
    .from('platform_books')
    .select('gst_registered')
    .eq('id', true)
    .maybeSingle();
  fail(error);
  return (data as { gst_registered?: unknown } | null)?.gst_registered === true;
}

export async function saveGstRegistered(sb: SupabaseClient, registered: boolean): Promise<void> {
  const { error } = await sb.from('platform_books').update({ gst_registered: registered }).eq('id', true);
  fail(error);
}
